import { OrgContext } from "contexts/OrgContext";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ComputeSelector from "components/Environment/Create/ComputeSelector";
import FlatCard from "components/UI-lib/FlatCard";
import ContainerSelector from "components/Environment/Create/ContainerSelector";
import { GPUInstanceType } from "components/Environment/Settings/Tabs/Compute/InstanceChanger/GPUTypes";
import InputField from "components/UI-lib/InputField";
import { classNames, validateName } from "components/utils";
import { RectangleGroupIcon } from "@heroicons/react/24/outline";
import Button from "components/UI-lib/Button";
import { BillingContext } from "contexts/BillingContext";
import {
  allPublicCloudsName,
  handleIsPaymentSetup,
  hasVerbBaseImageContainer,
  usesVerbYaml,
} from "components/Environment/Create/utils";
import { useFeatureFlagEnabled } from "posthog-js/react";
import FilterBar from "components/Environment/Create/ComputeSelector/Advanced/FilterBar";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";

export interface LaunchableComputeSelect {
  selectedInstanceType: GPUInstanceType | undefined;
  setSelectedInstanceType: (instanceType: GPUInstanceType) => void;
  storage: string;
  setStorage: (storage: string) => void;
}

const LaunchableComputeSelect: React.FC<LaunchableComputeSelect> = ({
  selectedInstanceType,
  setSelectedInstanceType,
  storage,
  setStorage,
}) => {
  const orgContext = useContext(OrgContext);

  //environment state
  const [container, setContainer] = useState<ContainerSelected | undefined>({
    vmBuild: {
      forceJupyterInstall: false,
    },
  });
  const [pythonVersion, setPythonVersion] = useState<string>("3.10");
  const [cudaVersion, setCudaVersion] = useState<string>("12.0.1");

  const [environmentName, setEnvironmentName] =
    useState<string>("awesome-gpu-name");
  const [nameFieldErrorMessage, setNameFieldErrorMessage] =
    useState<string>("");
  const [selectedCloudName, setSelectedCloudName] =
    useState(allPublicCloudsName);
  const [loading, setLoading] = useState(false);

  const [displayGPUInstances, setDisplayGPUInstances] = useState<
    GPUInstanceType[]
  >([]);

  useEffect(() => {
    if (
      selectedInstanceType?.workspace_groups &&
      selectedInstanceType.workspace_groups.length > 0
    ) {
      const firstWorkspaceGroup = selectedInstanceType.workspace_groups[0];
      if (firstWorkspaceGroup.id === "dgxc-gcp") {
        setStorage("512");
      } else {
        setStorage("256");
      }
    }
  }, [selectedInstanceType]);

  const handleCompletion = async () => {};

  const isDeployDisabled = useMemo(() => {
    return (
      orgContext.GPUInstancesAvailable.length === 0 ||
      Number(storage) < 100 ||
      Number(storage) > 16000
    );
  }, [orgContext.GPUInstancesAvailable, storage]);

  const filterForValidInstances = useMemo(() => {
    return orgContext.GPUInstancesAvailable.filter((gpu) => {
      return gpu.workspace_groups[0].id !== "dgxc-gcp";
    });
  }, [orgContext.GPUInstancesAvailable]);

  return (
    <div>
      <div className="flex flex-row justify-between">
        <div>
          <h2 className="text-primary text-xl font-semibold">
            Add your Compute
          </h2>
          <p className="text-secondary">
            Configure compute resources for your Launchable
          </p>
        </div>
        <div className="flex flex-row items-end">
          {container && !selectedInstanceType && (
            <FilterBar
              allGPUInstances={filterForValidInstances}
              setDisplayGPUInstances={setDisplayGPUInstances}
              selectedCloud={selectedCloudName}
              setSelectedCloud={setSelectedCloudName}
              containerSelected={container}
              displayGPUInstances={displayGPUInstances}
            />
          )}
        </div>
      </div>
      <hr className="w-[100%] my-3 dark:border dark:border-zinc-800" />
      <div className="flex flex-col">
        <AnimatePresence initial={false}>
          {container && (
            <>
              <ComputeSelector
                allGPUInstances={filterForValidInstances}
                isLoadingInstances={orgContext.isInstancesLoading}
                displayGPUInstances={displayGPUInstances}
                setDisplayGPUInstances={setDisplayGPUInstances}
                selectedInstance={selectedInstanceType}
                setSelectedInstance={setSelectedInstanceType}
                setStorage={setStorage}
                storage={storage}
                containerSelected={container}
                selectedCloudName={selectedCloudName}
                setSelectedCloudName={setSelectedCloudName}
                hideDgxc={true}
              />
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LaunchableComputeSelect;
